import {
  Box,
  DATE_FORMAT,
  HighlightIcon,
  type HighlightIconVariant,
  Icon,
  IconButton,
  type IconName,
  SkeletonText,
  Table,
  type TableMiniColumn,
  Tag,
  Tooltip,
} from '@dev-spendesk/grapes';
import type {
  PayableSubType,
  PayableType,
} from '@spendesk/bookkeeping-core-types';
import { type MonetaryValue } from 'ezmoney';
import { type ReactNode, useState } from 'react';

import { type RequestType } from 'modules/requests/models/requestType';
import { type APITravelStop } from 'modules/requests/models/tripStop';
import { getSubscriptionIntervalTranslation } from 'modules/requests/subscription/get-subscription-recurrence-translation';
import { getTripTitle } from 'modules/requests/utils/trips';
import { useFetchSupplier } from 'modules/suppliers/hooks';
import { UserAvatar } from 'modules/users/components';
import { unwrapQuery } from 'src/core/api/unwrapQuery';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { COUNTRIES } from 'src/core/config/country';
import { useGoToPayableDetails } from 'src/core/modules/payable';
import {
  getExpenditureAvatar,
  getExpenditureType,
} from 'src/core/utils/entities/expenditure';
import { metersToDistanceUnit } from 'src/core/utils/geo';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';
import { formatNumberToDistance } from 'src/core/utils/number';
import { TypeGuardError } from 'src/core/utils/switchGuard';

import { ExpenditurePanelHeaderSupplierForm } from './ExpenditurePanelHeaderSupplierForm';
import { CardPayment } from '../CardPayment/CardPayment';
import { CountryFlag } from '../CountryFlag/CountryFlag';
import { EllipsisTooltip } from '../EllipsisTooltip';

import './ExpenditurePanelHeader.css';

export type ExpenditureType = RequestType | 'plastic_card';

export type ExpenditurePanelHeaderProps = {
  user: {
    id: string;
    name: string | null | undefined;
    email: string | null | undefined;
    avatar: string | null | undefined;
    syncStatus?: ReactNode;
  };
  amountInCompanyCurrency: string;
  amountInExpenditureCurrency?: string;
  feesAmountInCompanyCurrency?: string;
  date: string | Date | null;
  showPayableLink?: boolean;
  payable?: {
    id: string;
    amount: MonetaryValue;
    date: string | Date;
    type: PayableType;
    subType: PayableSubType;
  };
  canCreateSupplier?: boolean;
  subscriptionRecurrence?: 'MONTHLY' | 'YEARLY' | 'QUARTERLY' | null;
} & (
  | {
      type?:
        | Exclude<ExpenditureType, 'mileage_allowance' | 'per_diem_allowance'>
        | undefined;
      cardId?: string;
      isAtmWithdrawal?: boolean;
      isRequest?: boolean;
      supplier: {
        id: string | undefined;
        name: string;
        url?: string;
        syncStatus?: ReactNode;
        isEditable?: boolean;
        isDetailsEditable?: boolean;
        withDetails?: boolean;
        needsSpanishDprCompliance?: boolean;
      };
      onSupplierChange?: (newSupplier: { id?: string; name: string }) => void;
      supplierFeedback?: (isEditing: boolean) => ReactNode;
    }
  | {
      type?: Extract<ExpenditureType, 'mileage_allowance'>;
      mileage: {
        distance:
          | { value: number; unit: 'km' | 'mi'; precision?: number }
          | string;
        manualInput?: boolean;
      };
    }
  | {
      type?: Extract<ExpenditureType, 'per_diem_allowance'>;
      perDiem: {
        tripDays: number;
      };
    }
  | {
      type?: Extract<ExpenditureType, 'travel_budget'>;
      travel: {
        route: APITravelStop[];
      };
    }
);

export const ExpenditurePanelHeader = ({
  user,
  type,
  amountInExpenditureCurrency,
  amountInCompanyCurrency,
  feesAmountInCompanyCurrency,
  date,
  showPayableLink,
  payable,
  canCreateSupplier,
  subscriptionRecurrence,
  ...props
  // eslint-disable-next-line sonarjs/cognitive-complexity
}: ExpenditurePanelHeaderProps) => {
  const { t, localeFormat } = useTranslation('global');
  const goToPayableDetails = useGoToPayableDetails();

  const supplier = 'supplier' in props ? props.supplier : undefined;
  const cardId = 'cardId' in props ? props.cardId : undefined;
  const isAtmWithdrawal =
    'isAtmWithdrawal' in props ? props.isAtmWithdrawal : undefined;
  const isRequest = 'isRequest' in props ? props.isRequest : undefined;
  const onSupplierChange =
    'onSupplierChange' in props ? props.onSupplierChange : undefined;
  const supplierFeedback =
    'supplierFeedback' in props ? props.supplierFeedback : undefined;

  const [showSupplierEdition, setShowSupplierEdition] = useState(false);

  const expenditureType = getExpenditureType(
    { type, cardId, isAtmWithdrawal },
    t,
  );

  const expenditureAvatar = getExpenditureAvatar(
    { type, size: 32, supplier, isRequest },
    t,
  );

  // Mileage specific
  if (type === 'mileage_allowance' && 'mileage' in props) {
    const { distance, manualInput } = props.mileage;

    const formattedDistance =
      typeof distance === 'string'
        ? distance || t('misc.na')
        : formatNumberToDistance({
            number: metersToDistanceUnit({
              distanceMeters: distance.value,
              distanceUnit: distance.unit,
              precision: distance.precision,
            }),
            distanceUnit: distance.unit,
            precision: distance.precision,
          });

    expenditureAvatar.avatarTitle =
      formattedDistance +
      (manualInput ? ` ${t('requests.panel.mileage.manualInput')}` : '');
  }

  // Per diem specific
  if (type === 'per_diem_allowance' && 'perDiem' in props) {
    expenditureAvatar.avatarTitle = t('misc.day', {
      count: props.perDiem?.tripDays,
    });
  }

  if (type === 'travel_budget' && 'travel' in props) {
    const route = props.travel.route;
    const routeTitle = getTripTitle(route, t);
    if (routeTitle) {
      expenditureAvatar.avatarTitle = routeTitle;
    }
  }

  const payableTypeIcon = payable && getPayableIconWithColor(payable.subType);

  return (
    <div className="flex w-full flex-col gap-8">
      <Box
        variant="secondary"
        className="relative flex min-h-[80px] flex-col justify-center gap-16 py-16"
      >
        <div className="flex items-center">
          <UserAvatar
            size={32}
            id={user.id}
            name={user.name ?? user.email ?? ''}
            avatar={user.avatar ?? undefined}
            className="mr-12"
          />
          <h3 className="text-content-primary title-xl min-w-[200px]">
            {user.name}
          </h3>
          <Tag variant="white" className="ml-auto">
            {expenditureType.type}
          </Tag>
          {type === 'subscription' && subscriptionRecurrence && (
            <Tag variant="white" className="ml-8">
              {getSubscriptionIntervalTranslation(t, subscriptionRecurrence)}
            </Tag>
          )}
        </div>
        {user.syncStatus}
      </Box>

      <Box
        variant="secondary"
        className="group flex min-h-[80px] flex-col gap-8 py-16"
      >
        {supplierFeedback?.(showSupplierEdition)}

        {showSupplierEdition ? (
          <>
            <ExpenditurePanelHeaderSupplierForm
              supplier={supplier}
              onSubmit={async (newSupplier) => {
                if (newSupplier.id !== supplier?.id) {
                  await onSupplierChange?.(newSupplier);
                }
                setShowSupplierEdition(false);
              }}
              onCancel={() => setShowSupplierEdition(false)}
              withDetails={supplier?.withDetails}
              isSupplierEditable={supplier?.isEditable}
              isSupplierDetailsEditable={supplier?.isDetailsEditable}
              withSupplierDprInformation={supplier?.needsSpanishDprCompliance}
              canCreateSupplier={canCreateSupplier}
            />
          </>
        ) : (
          <>
            <div className="flex min-h-48 flex-wrap items-center gap-40">
              <div
                className="flex flex-1 items-center"
                data-testid="expenditure-panel-avatar"
              >
                {expenditureAvatar.avatar}
                <div className="ExpenditurePanelHeader__truncated ml-12">
                  <Tooltip
                    content={
                      <div className="break-all">
                        {expenditureAvatar.avatarTitle}
                      </div>
                    }
                    placement="bottom"
                    triggerAsChild
                  >
                    <div className="text-content-primary title-xl">
                      {expenditureAvatar.avatarTitle}
                    </div>
                  </Tooltip>
                </div>
                {onSupplierChange && (
                  <IconButton
                    className="supplier-edit hidden w-0 flex-shrink-0 group-hover:block group-hover:w-40"
                    variant="tertiaryNeutral"
                    iconName="pen"
                    aria-label={t('misc.edit')}
                    onClick={() => setShowSupplierEdition(true)}
                  />
                )}
              </div>
              <div className="ml-auto flex items-center justify-end gap-4">
                <div
                  data-testid="expenditure-panel-amounts"
                  className="flex flex-col gap-4"
                >
                  <span className="flex flex-wrap items-center justify-end gap-8">
                    {amountInExpenditureCurrency && (
                      <>
                        <span className="text-content-secondary-bg-secondary title-xl whitespace-nowrap">
                          {`${amountInExpenditureCurrency}`}
                        </span>
                        <span className="text-content-disabled">•</span>
                      </>
                    )}
                    <span className="text-content-primary title-xl whitespace-nowrap">
                      {amountInCompanyCurrency}
                    </span>
                  </span>
                  <span className="text-content-secondary-bg-secondary body-m ml-auto whitespace-nowrap">
                    {date ? (
                      <>
                        {t('requests.panel.createdDate')}{' '}
                        {date instanceof Date
                          ? localeFormat(new Date(date), DATE_FORMAT.MEDIUM)
                          : date}
                      </>
                    ) : null}
                  </span>
                </div>
              </div>
            </div>

            {supplier?.syncStatus}

            {feesAmountInCompanyCurrency && (
              <>
                <div className="separator my-4" />
                <div className="flex items-center">
                  <div className="text-content-secondary-bg-secondary">
                    {t('common.includingFxFees')}
                  </div>
                  <span className="text-content-primary title-m ml-auto">
                    {feesAmountInCompanyCurrency}
                  </span>
                </div>
              </>
            )}

            {supplier?.withDetails && (
              <ExpenditurePanelHeaderSupplierDetails
                supplierId={supplier?.id}
              />
            )}
          </>
        )}
      </Box>

      {showPayableLink && payable && (
        <Box
          variant="secondary"
          className="relative flex min-h-[80px] flex-col justify-center gap-16 py-16"
        >
          <h3 className="title-m">
            {t('settlements.panel.allocatedToPayable')}
          </h3>
          <div className="flex items-center justify-between gap-12">
            {payableTypeIcon && (
              <HighlightIcon
                variant={payableTypeIcon.color}
                name={payableTypeIcon.name}
                size={32}
                className="shrink-0"
              />
            )}

            <CardPayment
              primarySlot={formatMonetaryValue({
                ...payable?.amount,
                amount: Math.abs(payable.amount.amount),
              })}
              secondarySlot={
                <>{localeFormat(new Date(payable.date), DATE_FORMAT.MEDIUM)}</>
              }
              tertiarySlot={<Icon name="chevron-right" />}
              onClick={() => {
                goToPayableDetails(payable.id);
              }}
            />
          </div>
        </Box>
      )}
    </div>
  );
};

const ExpenditurePanelHeaderSupplierDetails = ({
  supplierId,
}: {
  supplierId: string | undefined;
}) => {
  const { t } = useTranslation('global');

  const fetchSupplierQuery = useFetchSupplier(supplierId);
  const supplierData = unwrapQuery(fetchSupplierQuery);
  const { vatNumber, country } =
    (supplierData && {
      vatNumber: supplierData.supplierDetails?.legalInfos?.vatNumber,
      country: supplierData.supplierDetails?.address?.country,
    }) ??
    {};
  const isLoading = fetchSupplierQuery.status === 'loading';

  const data = [
    {
      id: 'vatNumber',
      label: t(
        'suppliers.spanishDprCompliance.missingSupplierDetailsCallout.vatNumber.label',
      ),
      value: (
        <span className="block w-[260px]">
          {vatNumber ? <EllipsisTooltip text={vatNumber} /> : '-'}
        </span>
      ),
    },
    {
      id: 'country',
      label: t(
        'suppliers.spanishDprCompliance.missingSupplierDetailsCallout.country.label',
      ),
      value: (
        <span className="flex items-center gap-8">
          {country ? (
            <>
              <CountryFlag country={country} className="h-[18px] w-[18px]" />
              {t(COUNTRIES[country]?.translationKey)}
            </>
          ) : (
            '-'
          )}
        </span>
      ),
    },
  ];

  const columns: TableMiniColumn<(typeof data)[number]>[] = [
    {
      id: 'label',
      renderCell: (cell) => (
        <span className="text-content-secondary-bg-primary block w-[120px]">
          <EllipsisTooltip text={cell.label} />
        </span>
      ),
    },
    {
      id: 'value',
      renderCell: (cell) =>
        isLoading ? (
          <span className="block w-[260px]">
            <SkeletonText width="150px" />
          </span>
        ) : (
          cell.value
        ),
    },
  ];

  return (
    <Table.Mini
      className="bg-background-primary-default my-8"
      columns={columns}
      data={data}
      getRowId={(row) => row.id}
    />
  );
};

export const getPayableIconWithColor = (
  payableSubType: PayableSubType,
): { name: IconName; color: HighlightIconVariant } | undefined => {
  switch (payableSubType) {
    case 'creditNote':
      return { name: 'receipt-uturn-left-top', color: 'peach' };
    case 'expense':
      return { name: 'receipt', color: 'lemon' };
    case 'invoice':
      return { name: 'invoice', color: 'peach' };
    case 'mileageAllowance':
      return { name: 'car', color: 'lemon' };
    case 'perDiemAllowance':
      return { name: 'globe-eu-africa', color: 'lemon' };
    case 'multiUseCard':
      return { name: 'card', color: 'purple' };
    case 'subscription':
      return { name: 'card-rounded-arrows', color: 'purple' };
    case 'physical':
      return { name: 'card', color: 'purple' };
    case 'singlePurchase':
      return { name: 'card', color: 'purple' };
    default:
      throw new TypeGuardError(
        payableSubType,
        `Unknown payableType type ${payableSubType}`,
      );
  }
};
